// AI ID scan — photograph the guest's passport / ID and prefill the check-in form.
import React, { useRef, useState } from 'react';
import { Camera, Check, Loader2, ScanLine, X } from 'lucide-react';
import { API_URL, getToken, readCookie } from './_shared';

const LABELS = {
  first_name: 'First name', last_name: 'Last name', document_type: 'Document', document_number: 'Document no.',
  nationality: 'Nationality', issuing_country: 'Issued by', date_of_birth: 'Date of birth', expiry_date: 'Expires', sex: 'Sex',
};

const IdScanPanel = ({ bookingId, onApplied, onClose, mode = 'primary', onCompanion }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState(null);
  const [applyName, setApplyName] = useState(true);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);

  const scan = async (file) => {
    if (!file) return;
    setBusy(true); setErr(''); setFields(null);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = getToken();
      const csrf = readCookie('csrf_token');
      const r = await fetch(`${API_URL}/api/admin/reception/folios/${bookingId}/scan-id`, {
        method: 'POST', body: fd, credentials: 'include',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Scan failed');
      setFields(data.fields);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (mode === 'companion') { const { confidence, ...rest } = fields; onCompanion?.(rest); return; }
    setBusy(true); setErr('');
    try {
      const token = getToken();
      const csrf = readCookie('csrf_token');
      const r = await fetch(`${API_URL}/api/admin/reception/folios/${bookingId}/guest-doc`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
        body: JSON.stringify({ ...fields, apply_name: applyName }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Save failed');
      onApplied?.(data, fields);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const rows = fields && Object.keys(LABELS).map((k) => (
    <label key={k} className="text-xs text-slate-600">
      {LABELS[k]}
      <input value={fields[k] || ''} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} className="mt-0.5 w-full border rounded px-2 py-1 text-sm text-slate-900" data-testid={`idscan-field-${k}`} />
    </label>
  ));

  return (
    <div className="border border-sky-200 bg-sky-50 rounded-lg p-3 space-y-3" data-testid="idscan-panel">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-sky-900 flex items-center gap-1"><ScanLine className="w-4 h-4" /> Scan passport / ID</span>
        <button onClick={onClose} className="p-1 rounded hover:bg-white/60" data-testid="idscan-close"><X className="w-4 h-4" /></button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => scan(e.target.files?.[0])} data-testid="idscan-input" />
      <div className="flex items-center gap-3">
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="flex items-center gap-2 px-3 py-2 rounded bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50" data-testid="idscan-take">
          {busy && !fields ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {fields ? 'Scan again' : 'Take photo / choose image'}
        </button>
        {preview && <img src={preview} alt="document" className="h-12 rounded border object-cover" />}
        {busy && !fields && <span className="text-xs text-sky-800">Reading document…</span>}
      </div>
      {err && <p className="text-xs text-red-600" data-testid="idscan-error">{err}</p>}
      {fields && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="idscan-fields">{rows}</div>
          {fields.confidence != null && <p className="text-[11px] text-slate-500">Model confidence {Math.round(fields.confidence * 100)}% — please verify against the document.</p>}
          <div className="flex items-center justify-between gap-2">
            {mode === 'primary'
              ? <label className="text-xs text-slate-700 flex items-center gap-1"><input type="checkbox" checked={applyName} onChange={(e) => setApplyName(e.target.checked)} data-testid="idscan-apply-name" /> Also update guest name</label>
              : <span className="text-xs text-slate-600">Will be added as a companion</span>}
            <button onClick={apply} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 rounded bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50" data-testid="idscan-apply">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {mode === 'companion' ? 'Add companion' : 'Use these details'}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">The photo is analysed and discarded — only the typed details are saved to the folio.</p>
        </>
      )}
    </div>
  );
};

export default IdScanPanel;
